/** The posting's own text, read into headings, paragraphs, lists and labelled values. */

import { plainText } from "@/lib/format";

/** One piece of a posting's text, as the catalogue lays it out. */
export type TextBlock =
    | { kind: "heading"; text: string }
    | { kind: "paragraph"; lines: string[] }
    | { kind: "list"; items: string[] }
    | { kind: "field"; label: string; value: string };

/** The longest a line is and still reads as a heading. */
const HEADING_MAX = 80;

/** The longest a line is and still reads as a label's value: "Varied", "Full time". */
const VALUE_MAX = 40;

/** A line that ends no sentence, ends in punctuation that would. */
const SENTENCE_END = /[.!?,;:…]$/;

/** A short line ending in a colon: "Your Job Requirements:", "Work Shift :". */
function asLabel(line: string): string | null {
    return line.length <= HEADING_MAX && line.endsWith(":") ? line.replace(/\s+:$/, ":") : null;
}

function isLabel(block: TextBlock | undefined): boolean {
    return block?.kind === "heading" && block.text.endsWith(":");
}

/**
 * The posting's text in the shape the catalogue keeps it: paragraphs a
 * blank line apart, a line break inside one kept, and each list item a line
 * of its own starting `- `. A short line ending in a colon is a label and
 * starts a part of its own, so "Your Job:" heads its part wherever it sits.
 * A label with a short value after it is one field: "Days Of Week: Varied".
 * A label with no body of its own was left blank in the posting's form and
 * is not shown: one before a field, at the end, or before a section and its
 * first part ("Work Shift:" before "Job Description:", "Your Job:"). A paragraph of one short line
 * that ends no sentence, with more after it, is a heading: "About the
 * role". Text kept as one line of words, as the catalogue once kept every
 * posting, is one paragraph.
 */
export function textBlocks(description: string): TextBlock[] {
    const blocks: TextBlock[] = [];
    const push = (block: TextBlock) => {
        const last = blocks[blocks.length - 1];
        // A list a blank line breaks is still one list.
        if (block.kind === "list" && last?.kind === "list") last.items.push(...block.items);
        else blocks.push(block);
    };
    for (const chunk of description.replace(/\r\n?/g, "\n").split(/\n[ \t]*\n/)) {
        let lines: string[] = [];
        let items: string[] = [];
        const flush = () => {
            if (lines.length) push({ kind: "paragraph", lines });
            if (items.length) push({ kind: "list", items });
            lines = [];
            items = [];
        };
        for (const raw of chunk.split("\n")) {
            const line = raw.replace(/\s+/g, " ").trim();
            if (!line) continue;
            const item = /^- (.+)$/.exec(line);
            const label = item ? null : asLabel(line);
            if (item) {
                if (lines.length) flush();
                items.push(item[1]);
            } else if (label) {
                flush();
                push({ kind: "heading", text: label });
            } else {
                if (items.length) flush();
                lines.push(line);
            }
        }
        flush();
    }
    return labelled(blocks).map((block, index, all) =>
        block.kind === "paragraph" &&
        block.lines.length === 1 &&
        block.lines[0].length <= HEADING_MAX &&
        !SENTENCE_END.test(block.lines[0]) &&
        index < all.length - 1
            ? { kind: "heading", text: block.lines[0] }
            : block,
    );
}

/** A short line that ends no sentence: "About JSSI", over a paragraph's own words. */
function isTitleLine(line: string): boolean {
    return line.length <= HEADING_MAX && !SENTENCE_END.test(line);
}

/**
 * What a posting says first, for a card's one line: its paragraphs, with no
 * heading, label, list or title line in the way. "ABOUT ROGUEBEAM" and
 * "Type of Requisition: Regular" name a part and say nothing of the job.
 * A posting with no paragraph of words gives all it has.
 */
export function openingText(description: string): string {
    const lines = textBlocks(description).flatMap((block) => {
        if (block.kind !== "paragraph") return [];
        const from = block.lines.findIndex((line) => !isTitleLine(line));
        return from < 0 ? [] : block.lines.slice(from);
    });
    return lines.length > 0 ? lines.join(" ") : plainText(description);
}

/** A paragraph of one short line that ends no sentence: a label's value. */
function valueOf(block: TextBlock | undefined): string | null {
    if (block?.kind !== "paragraph" || block.lines.length !== 1) return null;
    const line = block.lines[0];
    return line.length <= VALUE_MAX && !SENTENCE_END.test(line) ? line : null;
}

/** A label in capitals names a section, so what follows it is never its value. */
function isSection(label: string): boolean {
    return label === label.toUpperCase() && /\p{Lu}/u.test(label);
}

/** Each label joined to a short value after it, then each label left blank dropped. */
function labelled(blocks: TextBlock[]): TextBlock[] {
    const joined: TextBlock[] = [];
    for (let at = 0; at < blocks.length; at++) {
        const block = blocks[at];
        const value = valueOf(blocks[at + 1]);
        if (
            block.kind === "heading" &&
            isLabel(block) &&
            !isSection(block.text) &&
            value !== null
        ) {
            joined.push({ kind: "field", label: block.text, value });
            at++;
        } else joined.push(block);
    }
    // Of labels in a row, the last two are a section and its first part;
    // any before them, or before a field, or at the end, were left blank.
    // Where each run ends is found from the back in one pass, so a run of
    // any length is read in time in proportion to it.
    const runEnd: number[] = new Array(joined.length + 1);
    runEnd[joined.length] = joined.length;
    for (let at = joined.length - 1; at >= 0; at--) {
        runEnd[at] = isLabel(joined[at]) ? runEnd[at + 1] : at;
    }
    return joined.filter((block, at) => {
        if (!isLabel(block)) return true;
        const end = runEnd[at];
        const after = joined[end];
        return after !== undefined && after.kind !== "field" && end - at <= 2;
    });
}
